import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

const components: Components = {
  h1: ({ children }) => (
    <h1 className="mt-4 first:mt-0 mb-2 text-fg uppercase tracking-[0.08em] text-[15px] border-b border-line pb-1">
      <span className="text-accent">#</span> {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mt-4 first:mt-0 mb-1.5 text-fg uppercase tracking-[0.06em] text-[14px]">
      <span className="text-accent">##</span> {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-3 first:mt-0 mb-1 text-fg-soft uppercase tracking-[0.05em] text-[13.5px]">
      <span className="text-accent">###</span> {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="mt-3 first:mt-0 mb-1 text-fg-soft text-[13.5px]">
      <span className="text-accent">####</span> {children}
    </h4>
  ),
  h5: ({ children }) => (
    <h5 className="mt-2 first:mt-0 mb-1 text-fg-soft text-[13px]">{children}</h5>
  ),
  h6: ({ children }) => (
    <h6 className="mt-2 first:mt-0 mb-1 text-fg-mute text-[13px]">{children}</h6>
  ),
  p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="text-accent underline underline-offset-2 hover:text-fg"
    >
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="text-fg font-semibold">{children}</strong>,
  em: ({ children }) => <em className="text-fg-soft italic">{children}</em>,
  del: ({ children }) => <del className="text-fg-mute">{children}</del>,
  ul: ({ children }) => (
    <ul className="my-2 pl-5 space-y-0.5 list-none [&>li]:relative [&>li]:before:content-['-'] [&>li]:before:absolute [&>li]:before:-left-4 [&>li]:before:text-accent">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="my-2 pl-6 space-y-0.5 list-decimal marker:text-accent marker:tnum">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="leading-[1.55]">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-line-strong pl-3 text-fg-soft italic">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-3 border-0 border-t border-line" />,
  code: ({ inline, className, children, ...props }: any) => {
    if (inline) {
      return (
        <code
          className="px-1 py-[1px] bg-surface border border-line text-fg text-[0.92em]"
          {...props}
        >
          {children}
        </code>
      )
    }
    return (
      <code className={className} {...props}>
        {children}
      </code>
    )
  },
  pre: ({ children }) => (
    <pre className="my-2 panel-inset px-3 py-2 overflow-x-auto text-[13px] leading-[1.55] whitespace-pre">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="border border-line text-[13.5px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-surface">{children}</thead>,
  th: ({ children }) => (
    <th className="border border-line px-2 py-1 text-left uppercase tracking-[0.05em] text-fg-soft text-[12px]">
      {children}
    </th>
  ),
  td: ({ children }) => <td className="border border-line px-2 py-1 align-top">{children}</td>,
}

export function Markdown({ text }: { text: string }) {
  return (
    <div className="text-fg leading-[1.6]">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  )
}
